/**
 * Settings → Field Payments & Photos (FIELD-PAY-01 + INSTALLER-APP-01b, Seth
 * 2026-09-24; everything admin-editable). Three things the installer's Wrap Up
 * reads:
 *   · cash in the field — on or off (org_setting field_cash_enabled);
 *   · the same-day turn-in amount (field_cash_turnin_over, default $200) and
 *     the office address the installer's Directions button opens (office_address);
 *   · the after-photo checklist (install_after_photos: [{label, required}]).
 */
import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson, useSaveOrgSetting } from "@/lib/orgSettings";
import { DEFAULT_AFTER_PHOTOS, type AfterPhoto } from "@/features/jobs/fieldPay";

export function FieldPaySettings() {
  const canEdit = useCan("settings.manage");
  const save = useSaveOrgSetting();
  const cash = useOrgSettingJson<boolean>("field_cash_enabled", true);
  const over = useOrgSettingJson<number>("field_cash_turnin_over", 200);
  const office = useOrgSettingJson<string>("office_address", "");
  const photos = useOrgSettingJson<AfterPhoto[]>("install_after_photos", DEFAULT_AFTER_PHOTOS);

  const [overText, setOverText] = useState("");
  const [addr, setAddr] = useState("");
  const [list, setList] = useState<AfterPhoto[]>([]);
  useEffect(() => { if (over.data != null) setOverText(String(over.data)); }, [over.data]);
  useEffect(() => { setAddr(typeof office.data === "string" ? office.data : ""); }, [office.data]);
  useEffect(() => { if (photos.data) setList(photos.data); }, [photos.data]);
  const listDirty = JSON.stringify(list) !== JSON.stringify(photos.data ?? []);
  const overNum = Number(overText);
  const overOk = overText.trim() !== "" && Number.isFinite(overNum) && overNum >= 0;

  const move = (i: number, d: -1 | 1) => setList((l) => {
    const n = [...l];
    const j = i + d;
    if (j < 0 || j >= n.length) return l;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          Cash in the Field
          <InfoTip label="How field cash works">When an installer takes cash, the office sees it right away on Today → Billing → Cash with the crews, until someone marks it received.</InfoTip>
        </h3>
        <label className="mt-3 flex items-center gap-3">
          <input type="checkbox" disabled={!canEdit || save.isPending} checked={cash.data !== false}
            onChange={(e) => save.mutate({ key: "field_cash_enabled", value: e.target.checked })}
            className="h-4 w-4 accent-brand-700" />
          <span className="text-sm text-slate-700">Installers can take cash</span>
        </label>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="block">
            <span className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              Bring it in the same day when over
              <InfoTip label="What happens over this amount">Over this amount, the installer&apos;s Today shows &ldquo;Bring cash to the office today&rdquo; until the office marks it received.</InfoTip>
            </span>
            <div className="mt-1 flex items-center gap-2">
              <div className="relative w-32">
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                <input type="number" min={0} value={overText} disabled={!canEdit} onChange={(e) => setOverText(e.target.value)}
                  aria-label="Bring it in the same day when over"
                  className="h-10 w-full rounded-lg border border-hairline pl-6 pr-2 text-right text-sm tabular-nums focus:border-brand-400 focus:outline-none" />
              </div>
              {canEdit && overOk && overNum !== Number(over.data) && (
                <Button size="sm" disabled={save.isPending} onClick={() => save.mutate({ key: "field_cash_turnin_over", value: overNum })}>Save</Button>
              )}
            </div>
          </div>
          <label className="block">
            <span className="text-xs font-semibold text-slate-500">Office address (for the Directions button)</span>
            <div className="mt-1 flex items-center gap-2">
              <input value={addr} disabled={!canEdit} onChange={(e) => setAddr(e.target.value)} placeholder="Street, City, MN"
                className="h-10 w-full rounded-lg border border-hairline px-3 text-sm focus:border-brand-400 focus:outline-none" />
              {canEdit && addr.trim() !== (office.data ?? "").trim() && (
                <Button size="sm" disabled={save.isPending} onClick={() => save.mutate({ key: "office_address", value: addr.trim() })}>Save</Button>
              )}
            </div>
          </label>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          After Photos
          <InfoTip label="Where this list shows">The installer&apos;s Wrap Up shows this list. Required photos are part of Ready to Finish; if one is missing, Install Complete asks why and tells the office.</InfoTip>
        </h3>
        <ul className="mt-3 space-y-2">
          {list.map((p, i) => (
            <li key={i} className="flex items-center gap-2">
              <input value={p.label} disabled={!canEdit} onChange={(e) => setList((l) => l.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))}
                className="h-9 min-w-0 flex-1 rounded-lg border border-hairline px-3 text-sm focus:border-brand-400 focus:outline-none" />
              <label className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-slate-600">
                <input type="checkbox" disabled={!canEdit} checked={p.required}
                  onChange={(e) => setList((l) => l.map((x, k) => (k === i ? { ...x, required: e.target.checked } : x)))} className="h-4 w-4 accent-brand-700" />
                Required
              </label>
              {canEdit && (
                <span className="flex shrink-0 items-center">
                  <button type="button" aria-label="Move up" onClick={() => move(i, -1)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" aria-label="Move down" onClick={() => move(i, 1)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><ArrowDown className="h-4 w-4" /></button>
                  <button type="button" aria-label="Remove" tabIndex={-1} onClick={() => setList((l) => l.filter((_, k) => k !== i))} className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </span>
              )}
            </li>
          ))}
        </ul>
        {canEdit && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setList((l) => [...l, { label: "", required: false }])}><Plus className="h-3.5 w-3.5" /> Add a Photo</Button>
            {listDirty && (
              <Button size="sm" disabled={save.isPending}
                onClick={() => save.mutate({ key: "install_after_photos", value: list.filter((p) => p.label.trim()).map((p) => ({ label: p.label.trim(), required: p.required })) })}>
                Save the List
              </Button>
            )}
          </div>
        )}
        {save.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t save — only an admin can change this.</p>}
      </Card>
    </div>
  );
}
